import * as XLSX from 'xlsx-js-style';
import {
  buildPhoneApiFields,
  parsePhoneForForm,
  resolveRegion,
  validatePhoneInput,
} from './phoneUtils';
import { locationService } from '../api/locationService';
import {
  KENYA_COUNTRY_ID,
  locationOptionId,
  locationOptionName,
  unwrapLocationList,
} from '../constants/kenyaLocations';

const DEFAULT_PASSWORD = 'DefaultPass123!';

/** Roles that persist user_locations (must supply region + county on bulk create). */
const LOCATION_ROLES = new Set(['agent', 'customer', 'team-lead']);

function normalizeHeader(header) {
  return String(header || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function parseOptionalId(value) {
  if (value == null || value === '') return null;
  const n = Number(String(value).trim());
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
}

function normalizeLocationLabel(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function findLocationByNameOrId(list, nameOrId) {
  if (nameOrId == null || nameOrId === '') return null;
  const raw = String(nameOrId).trim();
  if (!raw) return null;

  const asId = parseOptionalId(raw);
  if (asId != null) {
    const byId = list.find((item) => Number(locationOptionId(item)) === asId);
    if (byId) return byId;
  }

  const needle = normalizeLocationLabel(raw);
  return (
    list.find((item) => normalizeLocationLabel(locationOptionName(item)) === needle) ||
    list.find((item) => normalizeLocationLabel(locationOptionName(item)).includes(needle)) ||
    null
  );
}

function mapRowToParsedUser(row) {
  if (!row || typeof row !== 'object') return null;

  const normalized = {};
  Object.entries(row).forEach(([key, value]) => {
    normalized[normalizeHeader(key)] = value != null ? String(value).trim() : '';
  });

  const name =
    normalized.name ||
    normalized['full name'] ||
    normalized.fullname ||
    normalized.full_name ||
    '';
  const email =
    normalized.email ||
    normalized['email address'] ||
    normalized['e-mail'] ||
    '';
  const phone =
    normalized.phone ||
    normalized['phone number'] ||
    normalized.mobile ||
    '';
  // Do not treat location "region" as phone region — only explicit phone-region columns
  const phoneRegion =
    normalized['phone region'] ||
    normalized['country code'] ||
    '';
  const role = normalized.role || 'customer';
  const password =
    normalized.password ||
    normalized['default password'] ||
    normalized.defaultpassword ||
    DEFAULT_PASSWORD;

  // Prefer names; still accept legacy ID columns
  const regionName =
    normalized['region name'] ||
    (normalized.region && !/^\d+$/.test(normalized.region) ? normalized.region : '') ||
    '';
  const countyName =
    normalized['county name'] ||
    (normalized.county && !/^\d+$/.test(normalized.county) ? normalized.county : '') ||
    '';
  const subCountyName =
    normalized['sub county name'] ||
    normalized['subcounty name'] ||
    normalized.subcounty ||
    (normalized['sub county'] && !/^\d+$/.test(normalized['sub county'])
      ? normalized['sub county']
      : '') ||
    '';

  const regionId = parseOptionalId(
    normalized.regionid ||
      normalized['region id'] ||
      normalized.region_id ||
      (/^\d+$/.test(normalized.region || '') ? normalized.region : '')
  );
  const countyId = parseOptionalId(
    normalized.countyid ||
      normalized['county id'] ||
      normalized.county_id ||
      (/^\d+$/.test(normalized.county || '') ? normalized.county : '')
  );
  const subCountyId = parseOptionalId(
    normalized.subcountyid ||
      normalized['sub county id'] ||
      normalized['subcounty id'] ||
      normalized.sub_county_id ||
      (/^\d+$/.test(normalized['sub county'] || '') ? normalized['sub county'] : '')
  );

  if (!name || !email) return null;

  return {
    name,
    email: email.toLowerCase(),
    phone,
    phoneRegion: phoneRegion || null,
    role: normalizeRoleValue(role),
    password,
    regionName: regionName || null,
    countyName: countyName || null,
    subCountyName: subCountyName || null,
    regionId,
    countyId,
    subCountyId,
  };
}

export function normalizeRoleValue(role) {
  const r = String(role || 'customer')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/_/g, '-');
  if (r === 'enduser' || r === 'end-user') return 'customer';
  if (r === 'teamlead' || r === 'team_lead') return 'team-lead';
  if (r === 'superadmin' || r === 'super_admin') return 'super-admin';
  return r || 'customer';
}

export function resolveRoleFromList(roleValue, roles) {
  const normalized = normalizeRoleValue(roleValue);
  return roles.find((r) => {
    const value = (r.value || '').toLowerCase();
    const code = (r.roleCode || r.role_code || '')
      .toLowerCase()
      .replace(/_/g, '-');
    return value === normalized || code === normalized;
  });
}

export function buildRegistrationPayload(parsedRow, roles) {
  const selectedRole = resolveRoleFromList(parsedRow.role, roles);
  if (!selectedRole?.roleId) {
    throw new Error(`Invalid or unsupported role: ${parsedRow.role || 'unknown'}`);
  }

  const nameParts = parsedRow.name.trim().split(/\s+/).filter(Boolean);
  const email = parsedRow.email.trim().toLowerCase();

  const defaultRegion = resolveRegion(parsedRow.phoneRegion || 'KE');
  let phoneFields = { phone: '', phoneRegion: defaultRegion };
  if (parsedRow.phone?.trim()) {
    const parsed = parsePhoneForForm(parsedRow.phone, defaultRegion);
    phoneFields = buildPhoneApiFields(parsed.nationalNumber || parsedRow.phone, parsed.region);
  }

  const payload = {
    email,
    username: email.split('@')[0],
    fullName: parsedRow.name.trim(),
    firstName: nameParts[0] || '',
    lastName: nameParts.slice(1).join(' ') || '',
    phone: phoneFields.phone,
    phoneRegion: phoneFields.phoneRegion,
    roleId: selectedRole.roleId,
    defaultPassword: parsedRow.password?.trim() || DEFAULT_PASSWORD,
  };

  if (parsedRow.regionId != null) payload.regionId = parsedRow.regionId;
  if (parsedRow.countyId != null) payload.countyId = parsedRow.countyId;
  if (parsedRow.subCountyId != null) payload.subCountyId = parsedRow.subCountyId;

  return payload;
}

export function parseBulkUserCsv(text) {
  try {
    const workbook = XLSX.read(text, { type: 'string', raw: false });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) return [];
    const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: '' });
    return rows.map(mapRowToParsedUser).filter(Boolean);
  } catch {
    const lines = text.split(/\r?\n/).filter((line) => line.trim());
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map((h) => normalizeHeader(h));
    const users = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map((v) => v.trim());
      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      const mapped = mapRowToParsedUser(row);
      if (mapped) users.push(mapped);
    }
    return users;
  }
}

export async function parseBulkUserExcel(file) {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) return [];
  const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: '' });
  return rows.map(mapRowToParsedUser).filter(Boolean);
}

export async function parseBulkUserFile(file) {
  const fileName = file.name.toLowerCase();
  if (fileName.endsWith('.csv')) {
    const text = await file.text();
    return parseBulkUserCsv(text);
  }
  if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
    return parseBulkUserExcel(file);
  }
  throw new Error('Unsupported file type. Use CSV or Excel (.xlsx, .xls).');
}

/**
 * Resolve region/county/sub-county names (or legacy IDs) to IDs using master location APIs.
 */
async function resolveRowLocationIds(row, cache) {
  const next = { ...row };

  if (!cache.regions) {
    const regionsRes = await locationService.getActiveRegions();
    cache.regions = unwrapLocationList(regionsRes);
  }

  let regionId = next.regionId;
  if (next.regionName) {
    const region = findLocationByNameOrId(cache.regions, next.regionName);
    if (!region) {
      return { ok: false, reason: `Unknown region: ${next.regionName}` };
    }
    regionId = Number(locationOptionId(region));
  }

  if (!cache.countiesByRegion) cache.countiesByRegion = {};
  const countyLookupKey = regionId != null ? String(regionId) : 'all';
  if (!cache.countiesByRegion[countyLookupKey]) {
    if (regionId != null) {
      const countiesRes = await locationService.getActiveCounties(null, regionId);
      cache.countiesByRegion[countyLookupKey] = unwrapLocationList(countiesRes);
    } else {
      const countiesRes = await locationService.getActiveCounties(KENYA_COUNTRY_ID);
      cache.countiesByRegion[countyLookupKey] = unwrapLocationList(countiesRes);
    }
  }
  const counties = cache.countiesByRegion[countyLookupKey];

  let countyId = next.countyId;
  if (next.countyName) {
    const county = findLocationByNameOrId(counties, next.countyName);
    if (!county) {
      return { ok: false, reason: `Unknown county: ${next.countyName}` };
    }
    countyId = Number(locationOptionId(county));
    if (regionId == null && county.regionId != null) {
      regionId = Number(county.regionId);
    }
  }

  let subCountyId = next.subCountyId;
  if (countyId != null && next.subCountyName) {
    if (!cache.subCountiesByCounty) cache.subCountiesByCounty = {};
    const subKey = String(countyId);
    if (!cache.subCountiesByCounty[subKey]) {
      const subRes = await locationService.getActiveSubCounties(countyId);
      cache.subCountiesByCounty[subKey] = unwrapLocationList(subRes);
    }
    const sub = findLocationByNameOrId(cache.subCountiesByCounty[subKey], next.subCountyName);
    if (!sub) {
      return { ok: false, reason: `Unknown sub county: ${next.subCountyName}` };
    }
    subCountyId = Number(locationOptionId(sub));
  }

  next.regionId = regionId ?? null;
  next.countyId = countyId ?? null;
  next.subCountyId = subCountyId ?? null;
  return { ok: true, row: next };
}

/**
 * Sample Excel template with location names (resolved to IDs on upload).
 * Phone cells are written as text so Excel does not show scientific notation.
 */
export function downloadBulkUserTemplate() {
  const headers = ['name', 'email', 'phone', 'role', 'password', 'region', 'county', 'subCounty'];
  const rows = [
    ['John Doe', 'john.doe@example.com', '+254 712 345 678', 'customer', 'TempPass123!', 'Nairobi Province', 'Nairobi', 'Westlands'],
    ['Jane Smith', 'jane.smith@example.com', '+254 723 456 789', 'agent', 'TempPass123!', 'Nairobi Province', 'Nairobi', ''],
    ['Admin User', 'admin@example.com', '+254 734 567 890', 'admin', 'TempPass123!', '', '', ''],
  ];

  const aoa = [headers, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  const phoneCol = 2;
  for (let r = 1; r < aoa.length; r++) {
    const addr = XLSX.utils.encode_cell({ r, c: phoneCol });
    const cell = ws[addr];
    if (cell) {
      cell.t = 's';
      cell.v = String(rows[r - 1][phoneCol]);
      cell.z = '@';
    }
  }

  ws['!cols'] = [
    { wch: 14 },
    { wch: 28 },
    { wch: 18 },
    { wch: 10 },
    { wch: 14 },
    { wch: 18 },
    { wch: 12 },
    { wch: 14 },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Users');
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', 'user_registration_template.xlsx');
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function isDuplicateUserError(error) {
  const message = (
    error?.response?.data?.message ||
    error?.message ||
    ''
  ).toLowerCase();
  return (
    message.includes('already exists') ||
    message.includes('already registered') ||
    message.includes('email already') ||
    message.includes('username already') ||
    message.includes('phone')
  );
}

export function extractErrorText(error, fallback = 'Registration failed') {
  return error?.response?.data?.message || error?.message || fallback;
}

/**
 * Register users one-by-one via admin/super-admin API (supports roles + per-user duplicate handling).
 */
export async function registerBulkUsers(parsedUsers, roles, { checkEmail, checkPhone, registerUser }) {
  const created = [];
  const alreadyExists = [];
  const failed = [];
  const seenEmails = new Set();
  const seenPhones = new Set();
  const locationCache = {};

  for (const rawRow of parsedUsers) {
    const email = rawRow.email.trim().toLowerCase();

    if (seenEmails.has(email)) {
      alreadyExists.push({ ...rawRow, reason: 'Duplicate email in uploaded file' });
      continue;
    }
    seenEmails.add(email);

    let row = rawRow;
    try {
      const resolved = await resolveRowLocationIds(rawRow, locationCache);
      if (!resolved.ok) {
        failed.push({ ...rawRow, reason: resolved.reason });
        continue;
      }
      row = resolved.row;
    } catch (locErr) {
      failed.push({
        ...rawRow,
        reason: extractErrorText(locErr, 'Could not resolve location names'),
      });
      continue;
    }

    const role = normalizeRoleValue(row.role);
    if (LOCATION_ROLES.has(role) && (row.regionId == null || row.countyId == null)) {
      failed.push({
        ...row,
        reason: 'region and county are required for agent, customer, and team-lead (use names from master data)',
      });
      continue;
    }

    if (row.phone?.trim()) {
      const region = resolveRegion(row.phoneRegion || 'KE');
      const phoneValidation = validatePhoneInput(row.phone, region);
      if (!phoneValidation.valid) {
        failed.push({ ...row, reason: phoneValidation.message });
        continue;
      }
      const phoneFields = buildPhoneApiFields(row.phone, region);
      const e164Key = phoneFields.phoneE164 || `${region}:${row.phone}`;
      if (seenPhones.has(e164Key)) {
        alreadyExists.push({ ...row, reason: 'Duplicate phone in uploaded file' });
        continue;
      }
      seenPhones.add(e164Key);

      if (checkPhone) {
        try {
          const phoneAvailability = await checkPhone(
            phoneFields.phone,
            phoneFields.phoneRegion
          );
          if (phoneAvailability?.available === false) {
            alreadyExists.push({
              ...row,
              reason: phoneAvailability.message || 'Phone already exists',
            });
            continue;
          }
        } catch (checkErr) {
          failed.push({
            ...row,
            reason: extractErrorText(checkErr, 'Could not verify phone availability'),
          });
          continue;
        }
      }
    }

    try {
      const availability = await checkEmail(email);
      if (availability?.available === false) {
        alreadyExists.push({
          ...row,
          reason: availability.message || 'Email already exists',
        });
        continue;
      }
    } catch (checkErr) {
      failed.push({
        ...row,
        reason: extractErrorText(checkErr, 'Could not verify email availability'),
      });
      continue;
    }

    try {
      const payload = buildRegistrationPayload(row, roles);
      const response = await registerUser(payload);
      if (response?.data) {
        created.push({ ...row, email });
      } else {
        failed.push({ ...row, reason: 'No response from server' });
      }
    } catch (registerErr) {
      if (isDuplicateUserError(registerErr)) {
        alreadyExists.push({
          ...row,
          reason: extractErrorText(registerErr, 'User already exists'),
        });
      } else {
        failed.push({
          ...row,
          reason: extractErrorText(registerErr),
        });
      }
    }
  }

  return { created, alreadyExists, failed };
}

export function formatBulkRegistrationSummary(result) {
  const { created, alreadyExists, failed } = result;
  const parts = [];
  if (created.length) parts.push(`${created.length} created`);
  if (alreadyExists.length) parts.push(`${alreadyExists.length} already exist`);
  if (failed.length) parts.push(`${failed.length} failed`);
  return parts.join(', ') || 'No users processed';
}
